import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { GraduationCap, Search, MapPin, Mail, MessageSquare, Sun } from 'lucide-react';
import { InstructorAvatar } from '@/components/InstructorAvatar';
import SEO from '@/components/SEO';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';
import { SummerFoundingPartnerBadge } from '@/components/SummerFoundingPartnerBadge';
import { CommunityGrowthNote } from '@/components/CommunityGrowthNote';

// Full US state code → name. Used for headers in the Summer Founding
// Partners "by location" view so admins / browsers see "California"
// instead of just "CA".
const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

const STATE_SLUGS = {
  AL: 'alabama', AK: 'alaska', AZ: 'arizona', AR: 'arkansas', CA: 'california',
  CO: 'colorado', CT: 'connecticut', DE: 'delaware', DC: 'district-of-columbia',
  FL: 'florida', GA: 'georgia', HI: 'hawaii', ID: 'idaho', IL: 'illinois',
  IN: 'indiana', IA: 'iowa', KS: 'kansas', KY: 'kentucky', LA: 'louisiana',
  ME: 'maine', MD: 'maryland', MA: 'massachusetts', MI: 'michigan', MN: 'minnesota',
  MS: 'mississippi', MO: 'missouri', MT: 'montana', NE: 'nebraska', NV: 'nevada',
  NH: 'new-hampshire', NJ: 'new-jersey', NM: 'new-mexico', NY: 'new-york',
  NC: 'north-carolina', ND: 'north-dakota', OH: 'ohio', OK: 'oklahoma',
  OR: 'oregon', PA: 'pennsylvania', RI: 'rhode-island', SC: 'south-carolina',
  SD: 'south-dakota', TN: 'tennessee', TX: 'texas', UT: 'utah', VT: 'vermont',
  VA: 'virginia', WA: 'washington', WV: 'west-virginia', WI: 'wisconsin', WY: 'wyoming',
};

export default function InstructorsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Anonymous viewers can enter a zip to drive distance-based sort.
  // Logged-in users automatically use their own zip on the server.
  const [fromZip, setFromZip] = useState('');
  const [appliedZip, setAppliedZip] = useState('');
  const [viewerZip, setViewerZip] = useState('');
  // Toggle: when true, hide the normal list and instead render the
  // Summer Founding Partners grouped by state (location).
  const [sfpView, setSfpView] = useState(false);

  const loadInstructors = (zip) => {
    setLoading(true);
    const url = zip ? `/instructors?from_zip=${encodeURIComponent(zip)}` : '/instructors';
    api.get(url).then(({ data }) => {
      setInstructors(data.instructors || []);
      setViewerZip(data.viewer_zip || '');
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    // Re-fetch when the user explicitly applies a zip override; the
    // backend already prefers ?from_zip= over cookie auth.
    loadInstructors(appliedZip);
  }, [appliedZip]);

  // Backend already sorts by (distance, tier, name). Frontend just
  // filters by the search box without re-sorting.
  const filtered = instructors.filter(inst => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (inst.display_name || '').toLowerCase().includes(q) ||
      (inst.zip_code || '').includes(q) ||
      (inst.instructor_bio || '').toLowerCase().includes(q);
  });

  // Group Summer Founding Partners by US state — backend resolves
  // zip → state on the way out, so we just bucket. Falls back to an
  // "Other / Location unknown" group when the state can't be resolved.
  const sfpGroups = useMemo(() => {
    const sfp = instructors.filter(i => i.is_summer_founding_partner);
    const buckets = {};
    for (const i of sfp) {
      const key = (i.state || '').trim().toUpperCase() || '__UNKNOWN__';
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(i);
    }
    // Sort each bucket by display name; sort groups by state name.
    const sortedKeys = Object.keys(buckets).sort((a, b) => {
      if (a === '__UNKNOWN__') return 1;
      if (b === '__UNKNOWN__') return -1;
      return (STATE_NAMES[a] || a).localeCompare(STATE_NAMES[b] || b);
    });
    return sortedKeys.map(k => ({
      stateCode: k === '__UNKNOWN__' ? '' : k,
      stateName: k === '__UNKNOWN__' ? 'Location not set' : (STATE_NAMES[k] || k),
      instructors: buckets[k].sort((a, b) =>
        (a.display_name || '').localeCompare(b.display_name || '')
      ),
    }));
  }, [instructors]);
  const sfpTotal = sfpGroups.reduce((n, g) => n + g.instructors.length, 0);

  // Distinct US states that have at least one instructor in the
  // current dataset — drives the "Browse by state" footer panel that
  // gives Googlebot crawlable links into the state hub pages.
  const statesWithInstructors = (() => {
    const buckets = new Map();
    for (const i of instructors) {
      const code = (i.state || '').trim().toUpperCase();
      if (!code) continue;
      const slug = STATE_SLUGS[code];
      if (!slug) continue;
      const prev = buckets.get(code) || { code, slug, name: STATE_NAMES[code] || code, count: 0 };
      prev.count += 1;
      buckets.set(code, prev);
    }
    return [...buckets.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  // Card markup — shared between the "All instructors" grid and the
  // Summer Founding Partner grouped-by-state view. Kept inside the
  // component so it closes over `user` and `navigate`.
  const renderInstructorCard = (inst) => (
    <Card
      key={inst.id}
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/player/${inst.id}`)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/player/${inst.id}`);
        }
      }}
      className={`border-slate-100 hover:shadow-md transition-all duration-300 cursor-pointer focus-visible:ring-2 focus-visible:ring-[#b456df] focus-visible:outline-none ${inst.is_premium_instructor ? 'ring-1 ring-amber-300/60' : ''}`}
      data-testid={`instructor-card-${inst.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <InstructorAvatar
            photoUrl={inst.profile_photo}
            name={inst.display_name}
            testId={`instructor-avatar-${inst.id}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-lg hover:text-[#b456df]" style={{ fontFamily: 'Outfit, sans-serif' }}>{inst.display_name}</h3>
              {inst.is_premium_instructor && <PremiumInstructorBadge size="sm" />}
              {inst.is_summer_founding_partner && <SummerFoundingPartnerBadge size="sm" />}
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
              <span className="capitalize">{inst.skill_level} level</span>
              {(inst.city || inst.zip_code) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {inst.city ? `${inst.city}${inst.state ? `, ${inst.state}` : ''}` : inst.zip_code}
                </span>
              )}
              {typeof inst.distance_miles === 'number' && (
                <span
                  className="px-2 py-0.5 rounded-full bg-[#56c6df]/15 text-[#0f766e] text-xs font-medium"
                  data-testid={`instructor-distance-${inst.id}`}
                >
                  {inst.distance_miles === 0 ? 'Same zip' : `${inst.distance_miles} mi away`}
                </span>
              )}
            </div>
            {inst.instructor_bio && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{inst.instructor_bio}</p>}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(inst.instructor_lesson_types || []).map(t => (
                <span key={t} className="px-2 py-0.5 rounded-full bg-[#b456df]/10 text-[#b456df] text-xs capitalize">{t}</span>
              ))}
            </div>
            {inst.instructor_rate && <p className="text-xs text-slate-400 mt-2">Rate: {inst.instructor_rate}</p>}
            <div className="flex gap-2 mt-3" onClick={e => e.stopPropagation()}>
              {user && (
                <Link to={`/messages?to=${inst.id}`} onClick={e => e.stopPropagation()}>
                  <Button size="sm" className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] gap-1 text-xs">
                    <MessageSquare className="w-3 h-3" /> Message
                  </Button>
                </Link>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} onClick={e => e.stopPropagation()} data-testid={`instructor-email-${inst.id}`}>
                  <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs">
                    <Mail className="w-3 h-3" /> Email
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="instructors-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title="American Mahjong Instructors &amp; Lessons"
        description="Browse American Mahjong instructors offering group and private lessons. Find a teacher near you, learn NMJL rules, and sharpen your Mahjong skills."
        path="/instructors"
      />
      <div className="text-center mb-10">
        <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#b456df]">Instructors</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Find a Mahjong Instructor
        </h1>
        <p className="text-slate-500 max-w-xl mx-auto">Browse instructors offering group and private Mahjong lessons. Contact them directly to get started.</p>
      </div>

      {/* View toggle: normal list vs Summer Founding Partners by location */}
      <div
        className="flex flex-wrap items-center justify-center gap-2 mb-6"
        data-testid="instructors-view-toggle"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!sfpView}
          onClick={() => setSfpView(false)}
          className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
            !sfpView
              ? 'bg-[#b456df] text-white border-[#b456df]'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
          }`}
          data-testid="instructors-view-all"
        >
          All instructors
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sfpView}
          onClick={() => setSfpView(true)}
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
            sfpView
              ? 'text-white border-transparent shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
          }`}
          style={sfpView ? { backgroundImage: 'linear-gradient(135deg, #fbbf24, #f59e0b, #df5681, #b456df)' } : {}}
          data-testid="instructors-view-sfp"
        >
          <Sun className="w-3.5 h-3.5" />
          Summer Founding Partners by location
        </button>
      </div>

      {!sfpView && (
        <>
          <div className="max-w-md mx-auto mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, location, or specialty..." className="pl-10 rounded-full" data-testid="instructor-search" />
            </div>
          </div>

          {/* Distance sort control. Logged-in users already get distance
              sorting via their on-file zip; this input is mainly for
              anonymous visitors / users browsing from a different area. */}
          <div className="max-w-md mx-auto mb-8 flex items-center gap-2" data-testid="instructors-zip-row">
            <Input
              value={fromZip}
              onChange={e => setFromZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
              placeholder={viewerZip ? `Sort from zip (current: ${viewerZip})` : 'Enter your zip to sort by distance'}
              className="rounded-full"
              inputMode="numeric"
              data-testid="instructors-from-zip"
            />
            <Button
              onClick={() => setAppliedZip(fromZip)}
              disabled={fromZip.length !== 5}
              className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4]"
              data-testid="instructors-sort-by-distance"
            >
              Sort
            </Button>
            {appliedZip && (
              <Button
                variant="ghost"
                onClick={() => { setFromZip(''); setAppliedZip(''); }}
                className="rounded-full text-slate-500"
                data-testid="instructors-clear-zip"
              >
                Clear
              </Button>
            )}
          </div>
        </>
      )}

      <CommunityGrowthNote surface="instructors" />
      {!user && (
        <div className="mb-6 p-4 rounded-2xl bg-[#b456df]/5 border border-[#b456df]/20" data-testid="instructors-anon-cta">
          <p className="text-sm font-semibold text-slate-700 mb-2">Sign up free to get more from instructors:</p>
          <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside ml-1">
            <li>Send instructors a private message through the site</li>
            <li>Set up your own player profile so instructors can find you</li>
            <li>Get alerts when new instructors join near your zip</li>
          </ul>
          <p className="text-xs text-slate-500 mt-2">Anyone can browse and email instructors below — no account needed for that.</p>
          <Link to="/register" className="inline-block mt-3 text-sm font-semibold bg-clip-text text-transparent hover:underline" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}>
            Sign up free &rarr;
          </Link>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" /></div>
      ) : sfpView ? (
        sfpTotal === 0 ? (
          <div className="text-center py-16 px-6" data-testid="instructors-sfp-empty-state">
            <Sun className="w-12 h-12 mx-auto mb-4 text-amber-400" />
            <p className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No Summer Founding Partners yet
            </p>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
              Summer Founding Partner instructors will appear here, grouped by state, as they're added.
            </p>
          </div>
        ) : (
          <div data-testid="instructors-sfp-grouped">
            <p className="text-center text-sm text-slate-500 mb-6">
              {sfpTotal} Summer Founding {sfpTotal === 1 ? 'Partner' : 'Partners'} across {sfpGroups.length} {sfpGroups.length === 1 ? 'location' : 'locations'}
            </p>
            <div className="space-y-10">
              {sfpGroups.map(group => (
                <section
                  key={group.stateCode || '__unknown__'}
                  data-testid={`instructors-sfp-group-${group.stateCode || 'unknown'}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="h-[2px] flex-1 rounded-full"
                      style={{ backgroundImage: 'linear-gradient(90deg, transparent, #fbbf24, #f59e0b, #df5681, #b456df, transparent)' }}
                    />
                    <h2
                      className="text-lg font-bold tracking-tight inline-flex items-center gap-2 px-3"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      <MapPin className="w-4 h-4 text-[#df5681]" />
                      {group.stateName}
                      <span className="text-xs font-medium text-slate-400">
                        ({group.instructors.length})
                      </span>
                    </h2>
                    <div
                      className="h-[2px] flex-1 rounded-full"
                      style={{ backgroundImage: 'linear-gradient(90deg, transparent, #b456df, #df5681, #f59e0b, #fbbf24, transparent)' }}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {group.instructors.map(inst => renderInstructorCard(inst))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 px-6" data-testid="instructors-empty-state">
          <GraduationCap className="w-12 h-12 mx-auto mb-4 text-[#b456df] opacity-70" />
          <p className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {fromZip ? `No instructors near ${fromZip} yet` : 'No instructors match your search yet'}
          </p>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
            {fromZip
              ? <>We're a growing community — instructors are joining all the time. <span className="font-medium text-slate-700">If you teach,</span> set up a public instructor profile — players in this area are searching.</>
              : <>Try a broader zip or skill level. <span className="font-medium text-slate-700">If you teach,</span> set up a public instructor profile so players can find you.</>}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
            <Link to={user ? '/profile' : '/register?role=instructor'}>
              <Button className="rounded-full text-white font-semibold border-0 hover:brightness-110 transition-all" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }} data-testid="instructors-empty-become-cta">
                {user ? 'Become an instructor' : 'Sign up as an instructor'}
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map(inst => renderInstructorCard(inst))}
        </div>
      )}

      {/* State hub directory — crawlable internal links per state */}
      {statesWithInstructors.length > 0 && (
        <div
          className="mt-10 p-6 rounded-2xl border border-slate-100 bg-white"
          data-testid="instructors-state-hubs-panel"
        >
          <h3
            className="text-base font-semibold mb-1"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Browse American Mahjong instructors by state
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Jump to instructors in a specific state.
          </p>
          <div className="flex flex-wrap gap-2">
            {statesWithInstructors.map(s => (
              <Link
                key={s.code}
                to={`/instructors/${s.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#b456df]/8 text-[#b456df] hover:bg-[#b456df]/15 transition-colors"
                data-testid={`instructors-state-hub-link-${s.code.toLowerCase()}`}
              >
                {s.name}
                <span className="text-[10px] text-[#b456df]/70">({s.count})</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 text-center p-6 rounded-2xl bg-slate-50 border border-slate-100">
        <GraduationCap className="w-8 h-8 mx-auto text-[#b456df] mb-3" />
        <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Are you a Mahjong instructor?</h3>
        <p className="text-sm text-slate-500 mb-3">Create your public instructor profile and reach students across the country.</p>
        <Link to={user ? "/profile" : "/register"}>
          <Button className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-6" data-testid="become-instructor-btn">
            {user ? 'Set Up Instructor Profile' : 'Sign Up as Instructor'}
          </Button>
        </Link>
      </div>
    </div>
  );
}
